import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabaseGet } from '@/utils/supabaseRest';
import ScoringWidget from '@/components/scoring/ScoringWidget';

const emeraldLogoFilter = {
  filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)'
} as React.CSSProperties;

interface CampaignRow {
  id: string;
  user_id: string;
  chatbot_id: string;
  slug: string;
  name: string;
  mode: 'embedded' | 'standalone';
  flow_id: string | null;
  destinations: any;
  theme: any;
  status: 'draft' | 'published' | 'paused' | 'archived';
}

interface EntryIntent {
  primary_vertical: string;
  source: string;
  confidence: number;
}

function detectEntryIntent(): EntryIntent {
  const params = new URLSearchParams(window.location.search);
  const vertical = params.get('vertical') || params.get('utm_campaign') || 'auto_insurance';
  return {
    primary_vertical: vertical,
    source: params.get('utm_source') ? 'utm' : 'url',
    confidence: params.get('vertical') ? 0.9 : 0.5,
  };
}

export function StandaloneCampaign() {
  const { slug } = useParams<{ slug: string }>();
  const [campaign, setCampaign] = useState<CampaignRow | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoadingCampaign(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await supabaseGet<CampaignRow[]>(
          `campaigns?select=*&slug=eq.${encodeURIComponent(slug)}&mode=eq.standalone&limit=1`
        );

        if (cancelled) return;

        const row = data?.[0] ?? null;

        if (!row) {
          setNotFound(true);
        } else if (row.status === 'archived' || row.status === 'draft') {
          if (row.status === 'draft') {
            setNotFound(true);
          } else {
            setCampaign(row);
          }
        } else {
          setCampaign(row);
        }
      } catch (err) {
        console.error('Failed to load campaign:', err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoadingCampaign(false);
      }
    })();

    return () => { cancelled = true; };
  }, [slug]);

  // Loading state
  if (loadingCampaign) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-slate-300">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-400" />
          <p className="text-sm">Loading campaign...</p>
        </div>
      </div>
    );
  }

  // Not found / error
  if (notFound || !campaign) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <header className="p-4 md:p-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <img
              src="/logo.jpg"
              alt="IntentDrive.ai"
              className="h-10 w-auto rounded"
              style={emeraldLogoFilter}
            />
          </Link>
        </header>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <AlertTriangle className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-3">Campaign not found</h1>
            <p className="text-slate-400 mb-8">
              The campaign you're looking for doesn't exist, has been paused, or is no longer available.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors"
            >
              Back to IntentDrive.ai
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const entryIntent = detectEntryIntent();

  return (
    <div
      className="min-h-screen bg-slate-950 flex flex-col"
      data-testid="standalone-campaign"
      data-campaign-slug={campaign.slug}
    >
      {/* Header with logo */}
      <header className="px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <img
            src="/logo.jpg"
            alt="IntentDrive.ai"
            className="h-10 w-auto rounded"
            style={emeraldLogoFilter}
          />
        </Link>
        {campaign.name && (
          <span className="hidden sm:inline text-xs uppercase tracking-widest text-emerald-400/80">
            {campaign.name}
          </span>
        )}
      </header>

      {/* Scoring widget area */}
      <main className="flex-1 flex items-center justify-center px-3 sm:px-6 pb-8">
        <div className="w-full max-w-2xl h-[75vh] min-h-[520px] rounded-2xl overflow-hidden border border-emerald-500/20 shadow-[0_0_60px_-15px_rgba(16,185,129,0.35)] bg-slate-900">
          <ScoringWidget
            chatbotId={campaign.chatbot_id}
            entryIntent={entryIntent}
            embedded={true}
          />
        </div>
      </main>

      <footer className="px-4 md:px-8 py-4 text-center text-xs text-slate-500">
        Powered by{' '}
        <Link to="/" className="text-emerald-400 hover:text-emerald-300">
          IntentDrive.ai
        </Link>
      </footer>
    </div>
  );
}

export default StandaloneCampaign;
